<script>
    /*
    	VerticalResourceMenu.svelte

        11/4/22 - this is replacing the TaxonomyMenu.

        This dynamic menu will read in a collection of top-level Categories
        returned from a ZIP Code search.

        11/8 - this is now displayed under the SearchBar (no longer a top menu bar)
        
    */

    import { onMount } from 'svelte';
    import { fly } from 'svelte/transition';
    import { level0Node, level1Node, level2Node, level3Node, level4Node } from '../../stores/ResourceMenuStore.js';
    import { currentMenuNode, currentMenuLevel, currentMenuButton } from '../../stores/ResourceMenuStore.js';
    import {asyncValidatePostalCode, validatePostalCode} from "../../common/utils.js";
    import { setWarningAlert } from '../../stores/AlertsStore';
    import { isShowingZipError } from '../../stores/ScreenSettingsStore';
    import { masterSearch } from "../../common/master-objects";
    import { hasServiceIds, hasChildrenWithServiceIds } from '../../common/public-search-utils.js';


    // TCARE components
    import VerticalResourceMenuButton from './VerticalResourceMenuButton.svelte';

    // SMUI components

    // icon paths are the only non-dynamic assets that need to be specified
    // THESE KEYWORDS MUST MATCH CATEGORIES TO DISPLAY A BUTTON IN THE MENU!
    let buttons = [
        {keyword: 'food', iconPath: '/svg-icons/menu-icons-1/Food.svg', isSelected: false},
        {keyword: 'housing', iconPath: '/svg-icons/menu-icons-1/Housing.svg', isSelected: false},
        {keyword: 'goods', iconPath: '/svg-icons/menu-icons-1/Goods.svg', isSelected: false},
        {keyword: 'transportation', iconPath: '/svg-icons/menu-icons-1/Transit.svg', isSelected: false},
        {keyword: 'health', iconPath: '/svg-icons/menu-icons-1/Health.svg', isSelected: false},
        {keyword: 'finance', iconPath: '/svg-icons/menu-icons-1/Money.svg', isSelected: false},
        {keyword: 'care', iconPath: '/svg-icons/menu-icons-1/Care.svg', isSelected: false},
        {keyword: 'education', iconPath: '/svg-icons/menu-icons-1/Education.svg', isSelected: false},
        {keyword: 'work', iconPath: '/svg-icons/menu-icons-1/Work.svg', isSelected: false},
        {keyword: 'legal', iconPath: '/svg-icons/menu-icons-1/Legal.svg', isSelected: false}
    ]

    const NO_POSTAL_WARNING_MSG = 'Please enter a valid ZIP Code.';
    const NO_POSTAL_RESULTS_WARNING_MSG = 'Please run a ZIP Code search first.';

    export let searchStore;
    export let taxonomyName;                                            // OPEN_ELIG_2022
    export let onSearch;
    export let isAnimated;

    $: taxonomyObj = $searchStore.taxonomies[taxonomyName] || null;     // a JS Taxonomy object
    $: categoryNodesWithServices = getCategoryNodesWithServices(taxonomyObj);
    $: staticPostalCode = '';

    onMount(() => {
        // console.log('VerticalResourceMenu onMount');

        // select correct button if node was already clicked in another menu
        if ($level0Node) {
            // console.log('$level0Node: ' + $level0Node.keyword);
            let button = lookupButton($level0Node);
            if (button) {
                button.isSelected = true;
                categoryNodesWithServices = categoryNodesWithServices;      // refresh UI
            }
        }

        staticPostalCode = $searchStore.location.postalCode;
    })

    // top-level Taxonomy nodes that have services associated with them or children
    const getCategoryNodesWithServices = (taxObj) => {
        const nodes = [];
        if (!taxObj) return null;
        const topNodes = taxObj.getTopLevelItems();     // all of them regardless
        for (const node of topNodes) {
            if (hasServiceIds(node) || hasChildrenWithServiceIds(node)) {     // check both
                nodes.push(node);
            }
        }
        return nodes;
    }

    // newly selected button will be highlighted
    const onMenuButtonClick = async (node, button) => {
        // console.log('onMenuButtonClick: ' + node.label);

        const isValidZipCode = await asyncValidatePostalCode($masterSearch.location.postalCode)
        if (!isValidZipCode) {
            $isShowingZipError = true
            return false
        } else {
            $isShowingZipError = false
        }
        // this will not happen if the menu buttons are filtered by available services
        // if ($searchStore.postalResults.length === 0) {
        //     setWarningAlert(NO_POSTAL_RESULTS_WARNING_MSG, 1000);
        //     return;
        // }

        clearAllSelectedButtons();
        button.isSelected = true;
        buttons = buttons;                          // this line is needed for Svelte refresh of buttons

        taxonomyObj.selectAllNodes(false);          // clear out all others
        node.isSelected = true;

        $level0Node = node;                         // must update the store vars
        $currentMenuLevel = 0;
        $currentMenuNode = node;
        $currentMenuButton = button;

        if ($masterSearch.location.postalCode.length === 5) {
            onSearch();
        }
    }

    const clearAllSelectedButtons = () => {
        // menu only - not tax node related
        for (const button of buttons) {
            button.isSelected = false;
        }
        $level0Node = null;
        $level1Node = null;
        $level2Node = null;
        $level3Node = null;
        $level4Node = null;
    }

    
    // button.keyword must match node.keyword
    const lookupButton = (node) => {
        for (const button of buttons) {
            if (button.keyword === node.keyword) {
                node.menuLevel = 0;             // set as top-level node
                return button;
            }
        }
        return null;
    }
    

    /*
    // button.keyword must match node.keyword
    const lookupNode = (button) => {
        for (const node of categoryNodesWithServices) {
            console.log('lookupNode checking node: ' + node.keyword);
            if (button.keyword === node.keyword) {
                node.menuLevel = 0;             // set as top-level node
                return node;
            }
        }
        return null;
    }
    */

    
</script>

<style>
    .resource-menu-wrapper {
        width: 315px;
    }

    .resource-menu {
        position: relative;
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;
        background-color: white;
        /* border: 1px solid green; */
    }

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>

<!-- iterate nodes with child services to determine which menu buttons should display -->

<div class="resource-menu-wrapper">
    {#if isAnimated}
        <div class="resource-menu" in:fly="{{ y: -200, duration: 500 }}" out:fly="{{ y: -200, duration: 500 }}">
            {#each categoryNodesWithServices as node}
                {#if lookupButton(node)}
                    <VerticalResourceMenuButton {node} button={lookupButton(node)} onClick={onMenuButtonClick} />
                {/if}
            {/each}        
        </div>
    {:else}
        <div class="resource-menu">
            {#each categoryNodesWithServices as node}
                {#if lookupButton(node)}
                    <VerticalResourceMenuButton {node} button={lookupButton(node)} onClick={onMenuButtonClick} />
                {/if}
            {/each}        
        </div>
    {/if}
</div>


<!--
alternate way but not reactive

{#each buttons as button}
    {#if lookupNode(button)}
        <VerticalResourceMenuButton node={lookupNode(button)} {button} onClick={onMenuButtonClick} />
    {/if}
{/each}        
-->
